import { useId } from 'react';
import { HiOutlineGlobe } from 'react-icons/hi';

import { Icon } from '~uikit';

type Props = {
  locale: string;
  /** Rendered flag height in px (width follows the flag's aspect ratio). */
  size?: number;
};

/**
 * Small country flag for a UI locale, used by the language switcher. Inline SVG
 * (no dependency, crisp at any size, consistent across platforms — unlike flag
 * emoji, which don't render on Windows). Unknown locales fall back to a globe.
 */
export default function LocaleFlag({ locale, size = 14 }: Props) {
  switch (locale) {
    case 'fi':
      return <FinlandFlag size={size} />;
    case 'en':
      return <UnitedKingdomFlag size={size} />;
    default:
      return <Icon icon={HiOutlineGlobe} size="xsmall" color="muted1" />;
  }
}

// Finland — white field with a blue Nordic cross (official ratio 18:11).
function FinlandFlag({ size }: { size: number }) {
  const width = Math.round((size * 18) / 11);
  return (
    <svg
      width={width}
      height={size}
      viewBox="0 0 18 11"
      role="img"
      aria-label="Suomi"
      style={{ borderRadius: 2, display: 'block' }}
    >
      <rect width="18" height="11" fill="#fff" />
      <rect x="5" width="3" height="11" fill="#003580" />
      <rect y="4" width="18" height="3" fill="#003580" />
      <rect
        x="0.25"
        y="0.25"
        width="17.5"
        height="10.5"
        fill="none"
        stroke="rgba(0,0,0,0.15)"
        strokeWidth="0.5"
        rx="1"
      />
    </svg>
  );
}

// United Kingdom — Union Jack. Rendered at the same box size as the Finland
// flag (width 18:11) so every flag in the switcher matches; the viewBox keeps
// the Union Jack's own proportions within that box. Clip-path ids are
// per-instance so the flag can appear in the button and the menu at once
// without id clashes.
function UnitedKingdomFlag({ size }: { size: number }) {
  const id = useId().replace(/:/g, '');
  const width = Math.round((size * 18) / 11);
  return (
    <svg
      width={width}
      height={size}
      viewBox="0 0 60 30"
      role="img"
      aria-label="English"
      style={{ borderRadius: 2, display: 'block' }}
    >
      <clipPath id={`${id}-frame`}>
        <rect width="60" height="30" rx="2" />
      </clipPath>
      <clipPath id={`${id}-diag`}>
        <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
      </clipPath>
      <g clipPath={`url(#${id}-frame)`}>
        <rect width="60" height="30" fill="#012169" />
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
        <path
          d="M0,0 L60,30 M60,0 L0,30"
          clipPath={`url(#${id}-diag)`}
          stroke="#C8102E"
          strokeWidth="4"
        />
        <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
        <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
      </g>
    </svg>
  );
}
